/**
 * 経理確認ページ(2026-08-27)。支払い済み申請(報告型)の経理確認を、
 * 承認・支払い・受領と同じ作法で行う: 一覧+詳細ポップアップ+通知バッジ(stage=acct_confirm)。
 * 確認するとバッジが消え、承認者へ完了確認(④)の依頼が飛ぶ
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import { toShortDate, tsToShortDate } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import { fetchUsers } from '@core/masters/masterRepo';
import { fetchForReportAccountingConfirm } from './flowActions';
import { markReadExceptStage, markReadForTransaction, subscribeMyUnread } from './notificationsRepo';
import { StatusBadge } from './FlowParts';
import { useIsMobile } from '@core/lib/useIsMobile';
import ApplicationDetailModal from './ApplicationDetailModal';
import type { Transaction } from '@core/lib/types';

/** ページを行き来したとき前回の内容を即表示するためのキャッシュ(2026-08-28) */
const pageCache = new Map<string, { rows: Transaction[]; userNames: Map<string, string> }>();

export default function ReportConfirmPage() {
  const { user } = useAuth();
  const { accountName, subAccountName, departmentName, companyName } = useMasters();
  const toast = useToast();

  const cached = user ? pageCache.get(user.id) : undefined;
  const [rows, setRows] = useState<Transaction[]>(cached?.rows ?? []);
  const [userNames, setUserNames] = useState<Map<string, string>>(cached?.userNames ?? new Map());
  const [detail, setDetail] = useState<Transaction | null>(null);
  /** 「読み込み中…」は初回(キャッシュなし)だけ。再読込は画面を保ったまま裏で行う */
  const [loading, setLoading] = useState(!cached);
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [list, users] = await Promise.all([fetchForReportAccountingConfirm(), fetchUsers()]);
      const names = new Map(users.map((u) => [u.id, u.display_name]));
      setRows(list);
      setUserNames(names);
      pageCache.set(user.id, { rows: list, userNames: names });
      // 一覧に無い申請の経理確認依頼は既読にする(他の人が確認済み等)
      void markReadExceptStage(user.id, 'acct_confirm', new Set(list.map((t) => t.id)));
    } catch (e) {
      toast(e instanceof Error ? e.message : '一覧を取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // 通知が届いたら一覧も自動更新(他ページと同じ仕様)
  useEffect(() => {
    if (!user) return;
    let first = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const refresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void load(), 800);
    };
    const unsubscribe = subscribeMyUnread(user.id, () => {
      if (first) {
        first = false;
        return;
      }
      refresh();
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      if (timer) clearTimeout(timer);
    };
  }, [user, load]);

  // お知らせからの遷移: state.openId の詳細を自動で開く
  const location = useLocation();
  const openId = (location.state as { openId?: string } | null)?.openId ?? null;
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (!openId || autoOpened || rows.length === 0) return;
    const t = rows.find((r) => r.id === openId);
    if (t) {
      setDetail(t);
      setAutoOpened(true);
    }
  }, [openId, autoOpened, rows]);

  return (
    <section className="screen" style={{ maxWidth: 1360 }}>
      <div className="card">
        <h3>支払い済み申請確認(経理)</h3>
        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : rows.length === 0 ? (
          <p className="empty">経理確認待ちの支払い済み申請はありません。</p>
        ) : isMobile ? (
          /* スマホはA案カード表示(2026-08-28 選定) */
          <div>
            {/* カード全体をタップで詳細を開く(2026-08-28 指示。「詳細・操作」リンクは廃止) */}
            {rows.map((t) => (
              <div
                key={t.id}
                onClick={() => {
                  setDetail(t);
                  if (user) void markReadForTransaction(user.id, t.id);
                }}
                style={{
                  background: '#fff',
                  border: '1px solid var(--line)',
                  borderRadius: 10,
                  padding: '9px 12px',
                  marginBottom: 8,
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <b style={{ fontSize: 13 }}>{userNames.get(t.created_by) ?? '—'}</b>
                  <StatusBadge t={t} />
                  <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {formatYen(t.amount)}円 <span style={{ color: 'var(--muted)', fontWeight: 400 }}>›</span>
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, fontSize: 11, color: 'var(--muted)', flexWrap: 'wrap' }}>
                  <span>{t.account_id ? accountName(t.account_id) : '—'}</span>
                  <span>支払 {toShortDate(t.payment_date)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table className="tx-table">
            <thead>
              <tr>
                <th>報告日</th>
                <th>申請者</th>
                <th>会社</th>
                <th>事業部</th>
                <th>科目①</th>
                <th>科目②</th>
                <th>金額</th>
                <th>支払い日</th>
                <th>状態</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id}>
                  <td>{tsToShortDate(t.applied_at)}</td>
                  <td>{userNames.get(t.created_by) ?? '—'}</td>
                  <td className="hint">{companyName(t.company_id ?? 'sum')}</td>
                  <td>{departmentName(t.department_id)}</td>
                  <td className="hint">{t.account_id ? accountName(t.account_id) : '—'}</td>
                  <td className="hint">{t.sub_account_id ? subAccountName(t.sub_account_id) : '—'}</td>
                  <td className="num">{formatYen(t.amount)}</td>
                  <td>{toShortDate(t.payment_date)}</td>
                  <td>
                    <StatusBadge t={t} />
                  </td>
                  <td>
                    <button
                      className="btn-line"
                      style={{ padding: '5px 12px', fontSize: 12.5 }}
                      onClick={() => {
                        setDetail(t);
                        // お知らせ(info)のみ既読化。バッジは経理確認の実行で消える
                        if (user) void markReadForTransaction(user.id, t.id);
                      }}
                    >
                      詳細・操作
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detail && (
        <ApplicationDetailModal transaction={detail} onClose={() => setDetail(null)} onChanged={() => void load()} />
      )}
    </section>
  );
}
